/**
 * Auth middleware for React Router v7 SSR
 *
 * Verifies the `user_token` http-only JWT (EdDSA/Ed25519) and exposes
 * the userId (`sub` claim) on the React Router context. Verification
 * failures (missing, expired, invalid) are silently swallowed so the
 * request continues as an anonymous user.
 *
 * The public key is read from process.env at runtime (not baked at
 * build time) so it can be rotated without rebuilding the image.
 *
 * @see packages/pixai-server/src/service/auth.ts – verify3rdGenToken
 */
import { parseCookie } from 'cookie-es'
import * as jose from 'jose'
import { createContext, type MiddlewareFunction } from 'react-router'
import logger from 'src/utils/logger.server'
import { runWithRequestServerContext } from 'src/utils/request-context-als.server'
import { reportSsrCaughtError } from 'src/utils/ssr-caught-error.server'

// ============================================================================
// Auth Context
// ============================================================================

export interface AuthInfo {
  /** userId as a string (bigint serialized), or undefined for anonymous */
  userId: string | undefined
}

/**
 * React Router context for server-side auth state.
 * Use `context.get(authContext)` in loaders.
 */
export const authContext = createContext<AuthInfo>({ userId: undefined })

// ============================================================================
// JWT Verification
// ============================================================================

const COOKIE_NAME = 'user_token'
const EXPECTED_ISSUER = 'pixai'
const ALGORITHM = 'EdDSA' as const

/**
 * Lazily imported and memoized so importSPKI runs once per process,
 * not per request. A missing env var produces a single console.warn.
 */
let publicKeyPromise: Promise<CryptoKey> | null = null

function getPublicKey(): Promise<CryptoKey> {
  if (!publicKeyPromise) {
    const raw = process.env.JWT_3RD_GEN_PUBLIC_KEY_SPKI
    // Accept either full PEM or raw base64 — wrap if needed so
    // jose.importSPKI always receives a valid PEM string.
    const spki = raw?.startsWith('-----')
      ? raw.replaceAll('\\n', '\n')
      : raw
        ? `-----BEGIN PUBLIC KEY-----\n${raw}\n-----END PUBLIC KEY-----`
        : undefined
    if (!spki) {
      logger.warn(
        'JWT_3RD_GEN_PUBLIC_KEY_SPKI is not set — all requests will be anonymous',
      )
      publicKeyPromise = Promise.reject(
        new Error('JWT_3RD_GEN_PUBLIC_KEY_SPKI not configured'),
      )
      publicKeyPromise.catch(() => {}) // suppress unhandledRejection
    } else {
      publicKeyPromise = jose.importSPKI(spki, ALGORITHM)
    }
  }
  return publicKeyPromise
}

/** Extract the raw JWT from the Cookie header. */
function extractTokenFromCookies(cookieHeader: string): string | undefined {
  return parseCookie(cookieHeader)[COOKIE_NAME] || undefined
}

/** Verify JWT and return userId (sub claim). Never throws. */
async function extractUserId(token: string): Promise<string | undefined> {
  try {
    const publicKey = await getPublicKey()
    const { payload } = await jose.jwtVerify(token, publicKey, {
      algorithms: [ALGORITHM],
      issuer: EXPECTED_ISSUER,
      typ: 'JWT',
    })
    return typeof payload.sub === 'string' && payload.sub
      ? payload.sub
      : undefined
  } catch (err) {
    // jose throws JOSEError subclasses for routine user-side cases (expired
    // token, bad signature, malformed JWT) — those are expected on every
    // anonymous request and stay silent. Anything else (e.g. missing public
    // key) is a real ops issue worth surfacing.
    if (!(err instanceof jose.errors.JOSEError)) {
      reportSsrCaughtError(err, {
        origin: 'auth-middleware',
        operation: 'extractUserId',
      })
    }
    return undefined
  }
}

// ============================================================================
// Middleware
// ============================================================================

/**
 * Auth middleware — must run BEFORE experimentMiddleware so that
 * experiment evaluation can include userId for user-bucketed experiments.
 */
export const authMiddleware: MiddlewareFunction = async (
  { request, context },
  next,
) => {
  const cookieHeader = request.headers.get('cookie') ?? ''
  const token = extractTokenFromCookies(cookieHeader)

  const userId = token ? await extractUserId(token) : undefined
  context.set(authContext, { userId })

  // Expose the raw cookie + userId to anything that runs inside the
  // handler chain via AsyncLocalStorage. SSR-side data fetchers
  // (`serverGraphqlFetch` & friends) read from this so upstream
  // GraphQL calls run *as the current user* — without it, every SSR
  // call was anonymous and a logged-in owner couldn't see their own
  // private / training-in-progress models on the server-rendered
  // first paint. See `request-context-als.server.ts` for the
  // motivating bug and rollout notes.
  return runWithRequestServerContext({ cookieHeader, userId }, () => next())
}
